import React, { ReactNode } from 'react';

import { bemHelper } from '../../helpers';
import getGlobalProps, { GlobalProps } from '../../helpers/constants/global-props';
import useWindowWidth from '../../hooks/useWindowWidth';

export interface ControlProps {
  inputId?: string;
  children?: ReactNode;
  type?: '' | 'date' | 'file' | 'select' | 'switch' | 'textarea';
  size?: '' | 'full' | 'large' | 'half' | 'quarter' | 'auto';
  prefix?: ReactNode;
  suffix?: ReactNode;
  isCell?: boolean;
  isRadio?: boolean;
  isInline?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isCheckbox?: boolean;
  dataPrivate?: string | boolean;
  ['data-testid']?: string | number;
  isFilePicker?: boolean;
  globalProps?: GlobalProps;
}

/*
  Control is currently sunset, but passing sunset: true here would cause
  UI Health to add an outline to form elements that not actually sunset,
  but instead are just wrapped with Control.
*/
const internalGlobalProps = getGlobalProps({ inkSass: true });

function Control(props: ControlProps) {
  const { isMobile } = useWindowWidth();
  const { globalProps } = props;

  const defineClassName = () => {
    const { type, size, isCell, isInline, isInvalid, isDisabled, prefix, suffix } = props;
    const responsiveSize = isMobile ? 'full' : size;

    const b = bemHelper();

    return b('ink-control', {
      [`${type}`]: Boolean(type),
      /*
        In an effort to give all props explicit default values, as opposed to empty strings, we have changed
        the default "size" prop of several form components to "large." However, this breaks the Boolean("responsiveSize")
        logic, which means we have to explcitly disregard size === "large" on the following line.
      */
      [`${responsiveSize}`]: Boolean(responsiveSize) && size !== 'large' && !isCell,
      cell: isCell,
      inline: isInline,
      invalid: Boolean(isInvalid),
      disabled: Boolean(isDisabled),
      prefix: !!prefix,
      suffix: !!suffix,
    });
  };

  const definePrefixClassName = () => {
    const b = bemHelper();

    return b('ink-control--prefix', {
      cell: props.isCell,
    });
  };

  const defineSuffixClassName = () => {
    const b = bemHelper();

    return b('ink-control--suffix', {
      cell: props.isCell,
    });
  };

  const defineOutlineClassName = () => {
    const { type } = props;
    const b = bemHelper();

    return b('ink-control--outline', {
      triangle: !type,
    });
  };

  const shouldHaveAura = () => {
    const { isCell, isCheckbox, isFilePicker, isRadio } = props;
    return isCell && !(isCheckbox || isFilePicker || isRadio);
  };

  return (
    <div
      {...internalGlobalProps}
      className={defineClassName()}
      data-private={props.dataPrivate}
      data-testid={props['data-testid']}
      {...globalProps}
    >
      {Boolean(props.prefix) && <div className={definePrefixClassName()}>{props.prefix}</div>}
      {props.children}
      {shouldHaveAura() && (
        <>
          <div className={defineOutlineClassName()} />
          <label htmlFor={props.inputId} className="ink-control--aura" />
        </>
      )}
      {Boolean(props.suffix) && <div className={defineSuffixClassName()}>{props.suffix}</div>}
    </div>
  );
}

Control.defaultProps = {
  inputId: null,
  size: null,
  type: null,
  prefix: null,
  suffix: null,
  isCell: false,
  isRadio: false,
  isInline: false,
  isInvalid: false,
  isDisabled: false,
  isCheckbox: false,
  dataPrivate: undefined,
  'data-testid': undefined,
  isFilePicker: false,
};

export default Control;
